import { create } from 'zustand';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import {
  studioRecipesApi,
  type RecipeFetchResponse,
  type RecipeListEntry,
} from '../lib/studioRecipesApi';

interface StudioRecipesState {
  list: RecipeListEntry[];
  listLoading: boolean;
  listError: string | null;
  /** Currently loaded recipe for editing (null = no editor open). */
  current: RecipeFetchResponse | null;
  currentLoading: boolean;
  currentError: string | null;
  saving: boolean;
  saveError: string | null;
  drafting: boolean;
  draftError: string | null;
  loadList: () => Promise<void>;
  loadRecipe: (slug: string) => Promise<void>;
  /**
   * Open the editor with an in-memory draft (e.g. fresh from PDF or a
   * blank template). The recipe is NOT persisted until `save()` is called.
   */
  setDraft: (slug: string, recipe: StudioRecipe, source: 'user' | 'bundled') => void;
  clearCurrent: () => void;
  save: (slug: string, recipe: StudioRecipe) => Promise<void>;
  remove: (slug: string) => Promise<void>;
  draftFromPdf: (file: File, studio?: string) => Promise<{ recipe: StudioRecipe; warnings: string[] }>;
}

export const useStudioRecipesStore = create<StudioRecipesState>((set, get) => ({
  list: [],
  listLoading: false,
  listError: null,
  current: null,
  currentLoading: false,
  currentError: null,
  saving: false,
  saveError: null,
  drafting: false,
  draftError: null,
  async loadList() {
    set({ listLoading: true, listError: null });
    try {
      const list = await studioRecipesApi.list();
      set({ list });
    } catch (err) {
      set({ listError: (err as Error).message });
    } finally {
      set({ listLoading: false });
    }
  },
  async loadRecipe(slug) {
    set({ currentLoading: true, currentError: null });
    try {
      const data = await studioRecipesApi.get(slug);
      set({ current: data });
    } catch (err) {
      set({ currentError: (err as Error).message, current: null });
    } finally {
      set({ currentLoading: false });
    }
  },
  setDraft(slug, recipe, source) {
    set({
      current: { slug, source, recipe, status: 'ok', error: null },
      currentError: null,
    });
  },
  clearCurrent() {
    set({ current: null, currentError: null, saveError: null });
  },
  async save(slug, recipe) {
    set({ saving: true, saveError: null });
    try {
      const data = await studioRecipesApi.save(slug, recipe);
      set({
        current: {
          slug: data.slug,
          source: data.source,
          recipe: data.recipe,
          status: 'ok',
          error: null,
        },
      });
      // Refresh the list in the background so the source / mtime updates.
      void get().loadList();
    } catch (err) {
      set({ saveError: (err as Error).message });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
  async remove(slug) {
    await studioRecipesApi.remove(slug);
    // Clear the editor only if we were editing the slug we just deleted.
    if (get().current?.slug === slug) {
      // After delete, try reloading: if a bundled seed exists we'll show
      // that; otherwise the editor closes.
      try {
        const data = await studioRecipesApi.get(slug);
        set({ current: data });
      } catch {
        set({ current: null });
      }
    }
    void get().loadList();
  },
  async draftFromPdf(file, studio) {
    set({ drafting: true, draftError: null });
    try {
      const result = await studioRecipesApi.draftFromPdf(file, studio);
      return result;
    } catch (err) {
      set({ draftError: (err as Error).message });
      throw err;
    } finally {
      set({ drafting: false });
    }
  },
}));
